/* eslint-disable @typescript-eslint/no-explicit-any */
import * as R from 'ramda';
import { getQueueSelector } from '../../selectors/queue-selectors';
import { Queue } from '../../types/queue';
import { BoothTicket, APITicket } from '../../types/ticket';

export interface QueuesState {
  firstStatusReceived: boolean; // True if we processed CBX status message al least once since last refresh
  connectedQueues: Array<Queue>;
  selectedQueueId: string | null;
  currentTicket: BoothTicket | null;
  previousTickets: Array<BoothTicket>;
  boothStatus: 'unknown' | 'open' | 'closed';
  cbxUUID: null;
  boothName: string;
  boothCode: string;
}

const initialState: QueuesState = {
  firstStatusReceived: false, // True if we processed CBX status message al least once since last refresh
  connectedQueues: [],
  selectedQueueId: null,
  currentTicket: null,
  previousTickets: [],
  boothStatus: 'unknown',
  cbxUUID: null,
  boothName: '',
  boothCode: ''
};
export const STATUS_UPDATED = 'queues/status-updated';
export const BOOTH_STATUS_UPDATED = 'web-socket/booth/booth-status/cbx';
export const TICKET_INSERTED = 'queues/ticket-inserted';
export const SELECT_QUEUE = 'queues/select-queue';
export const SET_BOOTH_STATUS = 'queues/set-booth-status';

export const QUEUE_CLOSE_SUCCESS = 'queues/queue-close-success';
export const QUEUE_OPEN_SUCCESS = 'queues/queue-open-success';
export const CALL_NEXT_SUCCESS = 'web-socket/booth-call-next/cbx';
export const CALL_PREVIOUS_SUCCESS = 'web-socket/booth-call-previous/cbx';
export const CALL_AGAIN_SUCCESS = 'web-socket/booth-call-again/cbx';
export const CALL_SPECIFIC_SUCCESS = 'web-socket/booth-call-specific/cbx';

export const WS_REQUEST_SAGA = 'queues/ws-request-saga';
export const CALL_NEXT_REQUEST = 'booth-call-next';
export const CALL_PREVIOUS_REQUEST = 'booth-call-previous';
export const ADD_TICKET_REQUEST = 'booth/add-ticket';
export const UPDATE_TICKET_REQUEST = 'booth/update-ticket';
export const CALL_AGAIN_REQUEST = 'booth-call-again';
export const CALL_SPECIFIC_REQUEST = 'booth-call-specific';
export const REMOVE_SPECIFIC_REQUEST = 'booth-remove-specific';

export function openBooth() {
  return { type: SET_BOOTH_STATUS, payload: 'open' };
}

export function closeBooth() {
  return { type: SET_BOOTH_STATUS, payload: 'closed' };
}

export function selectQueue(queueId: string) {
  return { type: SELECT_QUEUE, queueId };
}

export function appendTicket(queueId: string, ticketNote: string) {
  return {
    type: WS_REQUEST_SAGA,
    cmd: ADD_TICKET_REQUEST,
    queueId,
    skipPrint: true,
    ticketNote
  };
}

export function updateTicket(queueId: string, ticket: APITicket) {
  return { type: WS_REQUEST_SAGA, cmd: UPDATE_TICKET_REQUEST, ticket, queueId };
}

export function callNext() {
  return { type: WS_REQUEST_SAGA, cmd: CALL_NEXT_REQUEST };
}

export function callPrevious() {
  return { type: WS_REQUEST_SAGA, cmd: CALL_PREVIOUS_REQUEST };
}

export function callAgain() {
  return { type: WS_REQUEST_SAGA, cmd: CALL_AGAIN_REQUEST };
}

export function callSpecificTicket(queueId: string, ticketUUID: string) {
  return {
    type: WS_REQUEST_SAGA,
    cmd: CALL_SPECIFIC_REQUEST,
    queueId,
    ticketUUID
  };
}

export function removeSpecificTicket(queueId: string, ticketUUID: string) {
  return {
    type: WS_REQUEST_SAGA,
    cmd: REMOVE_SPECIFIC_REQUEST,
    queueId,
    ticketUUID
  };
}

function updateQueue(queue: Queue, queues: Array<Queue>) {
  const idx = R.findIndex(R.propEq(queue.id, 'id'))(queues);
  if (idx === -1) {
    return queues;
  }
  return R.update(idx, queue)(queues);
}

export function queuesReducer(
  state: QueuesState = initialState,
  action: any = {}
): QueuesState {
  switch (action.type) {
    case 'ws/close/cbx': {
      return initialState;
    }
    case STATUS_UPDATED: {
      const connectedQueues = action.queues.map((q: Queue) => ({
        ...q,
        id: q.id
      }));
      return { ...state, connectedQueues, firstStatusReceived: true };
    }
    case BOOTH_STATUS_UPDATED: {
      const {
        currentTicket,
        previousTickets,
        status,
        name,
        code,
        uuid,
        authedDeviceUUID
      } = action;
      if (uuid !== authedDeviceUUID) {
        return state;
      }
      return {
        ...state,
        currentTicket,
        previousTickets,
        boothStatus: status,
        boothName: name,
        boothCode: code
      };
    }
    case SELECT_QUEUE: {
      const selectedQueue = getQueueSelector(action.queueId)(
        state.connectedQueues
      );
      if (!selectedQueue) {
        return state;
      }
      return { ...state, selectedQueueId: selectedQueue.id };
    }
    case QUEUE_CLOSE_SUCCESS: {
      const { queueId } = action;
      const selectedQueue = getQueueSelector(queueId)(state.connectedQueues);
      selectedQueue.status = 'closed';
      const connectedQueues = updateQueue(selectedQueue, state.connectedQueues);
      return { ...state, connectedQueues };
    }
    case QUEUE_OPEN_SUCCESS: {
      const { queueId, tickets } = action;
      const selectedQueue = getQueueSelector(queueId)(state.connectedQueues);
      selectedQueue.status = 'open';
      selectedQueue.tickets = tickets;
      const connectedQueues = updateQueue(selectedQueue, state.connectedQueues);
      return { ...state, connectedQueues };
    }
    case CALL_AGAIN_SUCCESS: {
      return { ...state };
    }
    case CALL_SPECIFIC_SUCCESS: {
      return { ...state };
    }
    case CALL_NEXT_SUCCESS: {
      return { ...state };
    }
    case CALL_PREVIOUS_SUCCESS: {
      return { ...state };
    }

    default: {
      return state;
    }
  }
}
